import React from "react";
import { AccessBehaviorProps } from "../AccessGate.types";
import { useModals } from "../../../context/ModalContext";
import type { DefaultSizes } from "../../../components/common/BaseModalComponent.d";

/**
 * Config accepted by ShowModalBehavior through `behaviorConfig`.
 *
 * - `component`      – React component rendered inside the BaseModalComponent body.
 * - `modalKey`       – unique key forwarded to `openModal`.
 * - `modalLabel`     – header title shown in the modal (default: "Action Required").
 * - `defaultSizes`   – forwarded to BaseModalComponent via the modal content map.
 * - `defaultFullscreen` – if true the modal opens fullscreen.
 * - `componentProps` – additional props spread into `component`.
 * - `tooltip`        – optional hint text shown on hover while blocked.
 */
export type ShowModalBehaviorConfig = {
    component: React.ComponentType<any>;
    modalKey: string;
    modalLabel?: string;
    defaultSizes?: DefaultSizes;
    defaultFullscreen?: boolean;
    componentProps?: Record<string, unknown>;
    tooltip?: string;
};

/**
 * Behavior that **blocks** the child element and opens a modal with a
 * configurable component when clicked. The child's original `onClick` is
 * completely replaced — it will not fire until the strategy condition is
 * fulfilled and `AccessGate` renders the children directly.
 *
 * The modal is opened programmatically via `useModals().openModal`, which
 * is the standard trigger-based pattern used by `BaseModalComponent`.
 *
 * @example
 * ```tsx
 * // Block a button until the company has a timezone configured
 * const access = useConfigStrategy(EConfigs.TIME_ZONE);
 *
 * <AccessGate
 *   strategy={access}
 *   behavior={ShowModalBehavior}
 *   behaviorConfig={{
 *     component: CompanyTimezoneModalContainer,
 *     modalKey: "Organization-TimeZone-Business",
 *     modalLabel: "Set Time Zone",
 *     tooltip: "A time zone must be configured first",
 *   }}
 * >
 *   <Button>Create Job</Button>
 * </AccessGate>
 * ```
 *
 * @example
 * ```tsx
 * // With custom sizes and extra props forwarded to the inner component
 * <AccessGate
 *   strategy={access}
 *   behavior={ShowModalBehavior}
 *   behaviorConfig={{
 *     component: MySetupWizard,
 *     modalKey: "setup-wizard",
 *     modalLabel: "Complete Setup",
 *     defaultSizes: { width: "60%", height: "70%" },
 *     componentProps: { step: 1 },
 *   }}
 * >
 *   <Card>Premium Feature</Card>
 * </AccessGate>
 * ```
 */
export function ShowModalBehavior({ children, config }: AccessBehaviorProps) {
    const {
        component: Component,
        modalKey = "access-gate-modal",
        componentProps = {},
        tooltip = "",
        onIntercept,
    } = (config ?? {}) as ShowModalBehaviorConfig & { onIntercept?: () => void };

    const { openModal } = useModals();

    const child = React.Children.only(children);

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (onIntercept) {
            onIntercept();
        } else if (Component && modalKey) {
            openModal(
                modalKey,
                <Component {...componentProps} />,
            );
        }
    };

    const clonedChild = React.cloneElement(child, {
        onClick: handleClick,
        style: {
            ...(child.props?.style ?? {}),
            opacity: 0.6,
            cursor: "pointer",
        },
        title: tooltip || child.props?.title,
    });

    return <>{clonedChild}</>;
}